import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  getTeams, getMarkets, getProducts,
  getTargetDefinitions, getTargetDefinition, getTargetFormOptions, getTargetResult,
  createTargetScenario, updateTargetScenario, deleteTargetScenario, exportTargetResult,
  distributeTarget, getTargetStatus, publishTarget, unpublishTarget,
} from '../services/adminService';

const fmtMonth = (v) => (v ? String(v).slice(0, 7) : '');
const fmtNum = (v, d = 0) =>
  v == null ? '—' : Number(v).toLocaleString('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d });

const STATUS_STYLE = {
  Draft:        { color: '#92400e', bg: '#fef3c7', label: 'Taslak' },
  Distributing: { color: '#7c3aed', bg: '#ede9fe', label: '⏳ Dağıtılıyor…' },
  Distributed:  { color: '#1d4ed8', bg: '#dbeafe', label: 'Dağıtıldı' },
  Published:    { color: '#15803d', bg: '#dcfce7', label: 'Yayınlandı 🔒' },
  Failed:       { color: '#b91c1c', bg: '#fee2e2', label: 'Başarısız' },
};

const EMPTY_FORM = {
  businessUnitId: '', marketId: '', productId: '', yearQuarter: '',
  versionName: '', nationalTarget: '', marketWeight: '', productWeight: '',
  calcMonths: [],
};

const TargetDistributionPanel = ({ tid, isSuperAdmin, setMessage, styles }) => {
  // ── Lookups ────────────────────────────────────────────────────────────────
  const [teams, setTeams]       = useState([]);
  const [markets, setMarkets]   = useState([]);
  const [products, setProducts] = useState([]);
  const [quarters, setQuarters] = useState([]);
  const [months, setMonths]     = useState([]);

  // ── List ─────────────────────────────────────────────────────────────────
  const [defs, setDefs]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [listError, setListError] = useState('');
  const [filterTeamId, setFilterTeamId] = useState('');

  // ── Create form ────────────────────────────────────────────────────────────
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [saving, setSaving]     = useState(false);
  const [formError, setFormError] = useState('');

  // ── Düzenle / sonuç genişletme ───────────────────────────────────────────
  const [editId, setEditId]         = useState(null);
  const [editForm, setEditForm]     = useState(null); // { versionName, nationalTarget, marketWeight, productWeight, calcMonths }
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError]   = useState('');
  const [resultRows, setResultRows] = useState([]);
  const [resultLoading, setResultLoading] = useState(false);
  const [exporting, setExporting]   = useState(false);
  const [busyId, setBusyId]         = useState(null);
  const prevDistributing = useRef(new Set());

  const companyProducts = products.filter(p => p.isAvixaProduct);
  const monthKey = (v) => String(v).slice(0, 7);

  const loadDefs = useCallback(async (teamId) => {
    if (!tid) { setDefs([]); return; }
    setLoading(true); setListError('');
    try {
      const data = await getTargetDefinitions(tid, {
        teamId: (teamId !== undefined ? teamId : filterTeamId) || undefined,
      });
      setDefs(data);
    } catch (err) {
      setListError(err?.response?.data?.message || 'Yükleme başarısız.');
    } finally {
      setLoading(false);
    }
  }, [tid, filterTeamId]);

  const reloadResult = useCallback(async (id) => {
    setResultLoading(true);
    try {
      setResultRows(await getTargetResult(id, tid));
    } catch {
      setResultRows([]);
    } finally {
      setResultLoading(false);
    }
  }, [tid]);

  // tid değişince lookups + liste yeniden yüklenir, formlar sıfırlanır.
  useEffect(() => {
    setForm(EMPTY_FORM); setShowForm(false); setEditId(null); setEditForm(null);
    setFilterTeamId('');
    if (!tid) { setTeams([]); setMarkets([]); setProducts([]); setQuarters([]); setMonths([]); setDefs([]); return; }
    Promise.all([
      getTeams(tid), getMarkets(tid), getProducts(tid), getTargetFormOptions(tid),
    ]).then(([t, m, p, opts]) => {
      setTeams(t); setMarkets(m); setProducts(p);
      setQuarters(opts.quarters || []); setMonths(opts.months || []);
    }).catch(() => setMessage('Form verileri yüklenemedi.'));
    loadDefs('');
  }, [tid]); // eslint-disable-line react-hooks/exhaustive-deps -- tid değişiminde tüm panel sıfırlanır

  // Arka plan dağıtımı: herhangi bir senaryo 'Distributing' iken listeyi periyodik tazele.
  // Distributing → Distributed/Failed geçişlerini yakalayıp kullanıcıya bildir; açık panelin sonucunu yenile.
  useEffect(() => {
    const nowDistributing = new Set(
      defs.filter(d => d.distributionStatus === 'Distributing').map(d => d.id)
    );
    prevDistributing.current.forEach(id => {
      if (!nowDistributing.has(id)) {
        const d = defs.find(x => x.id === id);
        if (!d) return;
        if (d.distributionStatus === 'Failed') {
          getTargetStatus(id, tid)
            .then(s => setMessage('Dağıtım başarısız: ' + (s.message || 'bilinmeyen hata')))
            .catch(() => setMessage('Dağıtım başarısız.'));
        } else if (d.distributionStatus === 'Distributed') {
          setMessage('Dağıtım tamamlandı.');
          if (editId === id) reloadResult(id);
        }
      }
    });
    prevDistributing.current = nowDistributing;

    if (nowDistributing.size === 0) return undefined;
    const t = setInterval(() => loadDefs(), 3000);
    return () => clearInterval(t);
  }, [defs]); // eslint-disable-line react-hooks/exhaustive-deps -- durum geçişlerini defs değişiminde izler

  // ── Create form handlers ───────────────────────────────────────────────────
  const setF = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const toggleFormMonth = (val) => setForm(f => ({
    ...f,
    calcMonths: f.calcMonths.includes(val) ? f.calcMonths.filter(x => x !== val) : [...f.calcMonths, val],
  }));
  const formWeightSum = (Number(form.marketWeight) || 0) + (Number(form.productWeight) || 0);

  const handleCreate = async () => {
    if (!form.businessUnitId || !form.marketId || !form.productId || !form.yearQuarter) {
      setFormError('Ekip, pazar, ürün ve hedef çeyrek zorunlu.'); return;
    }
    if (!form.nationalTarget || Number(form.nationalTarget) <= 0) {
      setFormError('National target sıfırdan büyük olmalı.'); return;
    }
    if (form.calcMonths.length === 0) { setFormError('En az bir referans ay seçilmeli.'); return; }
    setSaving(true); setFormError('');
    try {
      await createTargetScenario(tid, {
        versionName:    form.versionName || null,
        yearQuarter:    form.yearQuarter,
        businessUnitId: Number(form.businessUnitId),
        marketId:       Number(form.marketId),
        productId:      Number(form.productId),
        nationalTarget: Number(form.nationalTarget),
        marketWeight:   Number(form.marketWeight) || 0,
        productWeight:  Number(form.productWeight) || 0,
        calcMonths:     form.calcMonths,
      });
      setMessage('Senaryo oluşturuldu.');
      setForm(EMPTY_FORM); setShowForm(false);
      loadDefs();
    } catch (err) {
      setFormError(err?.response?.data?.message || 'Senaryo oluşturulamadı.');
    } finally {
      setSaving(false);
    }
  };

  // ── Row actions ──────────────────────────────────────────────────────────
  const runAction = async (id, fn, okMsg) => {
    setBusyId(id);
    try {
      await fn();
      setMessage(okMsg);
      if (editId === id) setEditId(null);
      loadDefs();
    } catch (err) {
      setMessage(err?.response?.data?.message || 'İşlem başarısız.');
    } finally {
      setBusyId(null);
    }
  };

  // Düzenle / Görüntüle: paneli aç, mevcut parametreleri + sonucu yükle.
  const openEdit = async (d) => {
    if (editId === d.id) { setEditId(null); setEditForm(null); return; }
    setEditId(d.id); setEditForm(null); setEditError(''); setResultRows([]); setResultLoading(true);
    try {
      const detail = await getTargetDefinition(d.id, tid); // { definition, calcMonths }
      const def = detail.definition || {};
      const selectedKeys = new Set((detail.calcMonths || []).map(monthKey));
      setEditForm({
        versionName:    def.versionName || '',
        nationalTarget: def.nationalTarget ?? '',
        marketWeight:   def.marketWeight ?? '',
        productWeight:  def.productWeight ?? '',
        calcMonths:     months.filter(m => selectedKeys.has(monthKey(m))),
      });
      setResultRows(await getTargetResult(d.id, tid));
    } catch (err) {
      setEditError(err?.response?.data?.message || 'Yüklenemedi.');
    } finally {
      setResultLoading(false);
    }
  };

  const setEF = (key, val) => setEditForm(f => ({ ...f, [key]: val }));
  const toggleEditMonth = (val) => setEditForm(f => ({
    ...f,
    calcMonths: f.calcMonths.includes(val) ? f.calcMonths.filter(x => x !== val) : [...f.calcMonths, val],
  }));

  const handleSaveEdit = async (id, thenDistribute) => {
    if (!editForm.nationalTarget || Number(editForm.nationalTarget) <= 0) {
      setEditError('National target sıfırdan büyük olmalı.'); return;
    }
    if (editForm.calcMonths.length === 0) { setEditError('En az bir referans ay seçilmeli.'); return; }
    setEditSaving(true); setEditError('');
    try {
      await updateTargetScenario(id, tid, {
        versionName:    editForm.versionName || null,
        nationalTarget: Number(editForm.nationalTarget),
        marketWeight:   Number(editForm.marketWeight) || 0,
        productWeight:  Number(editForm.productWeight) || 0,
        calcMonths:     editForm.calcMonths,
      });
      if (thenDistribute) {
        await distributeTarget(id, tid);
        setMessage('Kaydedildi, dağıtım başlatıldı — arka planda sürüyor…');
        setEditId(null); setEditForm(null);
      } else {
        setMessage('Senaryo güncellendi.');
      }
      await loadDefs();
    } catch (err) {
      setEditError(err?.response?.data?.message || 'İşlem başarısız.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleExport = async (id) => {
    setExporting(true);
    try {
      const resp = await exportTargetResult(id, tid);
      const blob = new Blob([resp.data], {
        type: resp.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const cd = resp.headers['content-disposition'] || '';
      const m = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(cd);
      const fileName = m ? decodeURIComponent(m[1]) : `HedefDagilimi_${id}.xlsx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fileName; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
    } catch {
      setMessage('Excel dışa aktarım başarısız.');
    } finally {
      setExporting(false);
    }
  };

  // ── Reusable month picker (düz fonksiyon — iç içe bileşen tanımından kaçınmak için) ──
  const renderMonthPicker = (selected, onToggle, disabled) => (
    months.length === 0
      ? <p style={{ color: '#94a3b8', fontSize: '13px' }}>Satış ayı bulunamadı.</p>
      : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
          {months.map(m => {
            const sel = selected.includes(m);
            return (
              <button key={m} type="button" disabled={disabled} onClick={() => onToggle(m)}
                style={{
                  padding: '4px 10px', borderRadius: '14px', fontSize: '12px',
                  cursor: disabled ? 'default' : 'pointer',
                  border: '1px solid ' + (sel ? '#1a1a2e' : '#d1d5db'),
                  backgroundColor: sel ? '#1a1a2e' : 'white', color: sel ? 'white' : '#475569',
                  opacity: disabled && !sel ? 0.5 : 1,
                }}>
                {fmtMonth(m)}
              </button>
            );
          })}
        </div>
      )
  );

  // ── Render ───────────────────────────────────────────────────────────────
  if (isSuperAdmin && !tid) {
    return <div style={styles.card}><p style={{ color: '#94a3b8' }}>Tenant seçin.</p></div>;
  }

  return (
    <div>
      {/* Toolbar + create form */}
      <div style={styles.card}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <h3 style={{ ...styles.cardTitle, margin: 0, flex: 1 }}>🎯 Hedef Dağılımı Senaryoları</h3>
          <select style={{ ...styles.input, minWidth: '160px' }} value={filterTeamId}
            onChange={e => { setFilterTeamId(e.target.value); loadDefs(e.target.value); }}>
            <option value="">Tüm Ekipler</option>
            {teams.map(t => <option key={t.id} value={t.id}>{t.teamName}</option>)}
          </select>
          <button style={styles.primaryButton} onClick={() => { setShowForm(s => !s); setFormError(''); }}>
            {showForm ? 'Vazgeç' : '+ Yeni Senaryo'}
          </button>
        </div>

        {showForm && (
          <div style={{ marginTop: '16px', padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <div>
                <label style={styles.label}>Ekip *</label>
                <select style={styles.input} value={form.businessUnitId} onChange={e => setF('businessUnitId', e.target.value)}>
                  <option value="">Seçin...</option>
                  {teams.map(t => <option key={t.id} value={t.id}>{t.teamName}</option>)}
                </select>
              </div>
              <div>
                <label style={styles.label}>Pazar *</label>
                <select style={styles.input} value={form.marketId} onChange={e => setF('marketId', e.target.value)}>
                  <option value="">Seçin...</option>
                  {markets.map(m => <option key={m.id} value={m.id}>{m.marketName}</option>)}
                </select>
              </div>
              <div>
                <label style={styles.label}>Ürün (firma) *</label>
                <select style={styles.input} value={form.productId} onChange={e => setF('productId', e.target.value)}>
                  <option value="">Seçin...</option>
                  {companyProducts.map(p => (
                    <option key={p.id} value={p.id}>{p.avixaProductName || p.imsProductName}</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={styles.label}>Hedef Çeyrek *</label>
                <select style={styles.input} value={form.yearQuarter} onChange={e => setF('yearQuarter', e.target.value)}>
                  <option value="">Seçin...</option>
                  {quarters.map(q => <option key={q} value={q}>{q}</option>)}
                </select>
              </div>
              <div>
                <label style={styles.label}>Senaryo Adı</label>
                <input style={styles.input} value={form.versionName}
                  onChange={e => setF('versionName', e.target.value)} placeholder="örn. Baz Senaryo" />
              </div>
              <div>
                <label style={styles.label}>National Target *</label>
                <input type="number" min="1" style={{ ...styles.input, minWidth: '120px' }} value={form.nationalTarget}
                  onChange={e => setF('nationalTarget', e.target.value)} />
              </div>
              <div>
                <label style={styles.label}>Pazar Ağırlığı (%)</label>
                <input type="number" step="0.01" min="0" style={{ ...styles.input, width: '110px', minWidth: '110px' }}
                  value={form.marketWeight} onChange={e => setF('marketWeight', e.target.value)} />
              </div>
              <div>
                <label style={styles.label}>Ürün Ağırlığı (%)</label>
                <input type="number" step="0.01" min="0" style={{ ...styles.input, width: '110px', minWidth: '110px' }}
                  value={form.productWeight} onChange={e => setF('productWeight', e.target.value)} />
              </div>
            </div>

            {(form.marketWeight !== '' || form.productWeight !== '') && formWeightSum !== 100 && (
              <p style={{ color: '#b45309', fontSize: '12px', margin: '8px 0 0 0' }}>
                Ağırlık toplamı {formWeightSum}% (genellikle 100% olur).
              </p>
            )}

            <div style={{ marginTop: '14px' }}>
              <label style={styles.label}>Referans Aylar * (satış hesabı için)</label>
              {renderMonthPicker(form.calcMonths, toggleFormMonth, false)}
              {form.calcMonths.length > 0 && (
                <p style={{ color: '#64748b', fontSize: '12px', margin: '6px 0 0 0' }}>{form.calcMonths.length} ay seçildi.</p>
              )}
            </div>

            {formError && <p style={{ color: '#dc2626', fontSize: '13px', margin: '10px 0 0 0' }}>{formError}</p>}

            <div style={{ marginTop: '14px', display: 'flex', gap: '8px' }}>
              <button style={styles.primaryButton} disabled={saving} onClick={handleCreate}>
                {saving ? 'Kaydediliyor...' : 'Senaryo Oluştur'}
              </button>
              <button style={styles.pageButton} onClick={() => { setShowForm(false); setForm(EMPTY_FORM); setFormError(''); }}>
                İptal
              </button>
            </div>
          </div>
        )}
      </div>

      {/* List */}
      <div style={styles.card}>
        {loading   && <p style={{ color: '#64748b', padding: '8px 0' }}>Yükleniyor...</p>}
        {listError && <p style={{ color: '#dc2626', padding: '8px 0' }}>{listError}</p>}
        {!loading && !listError && defs.length === 0 && (
          <p style={{ color: '#94a3b8', fontSize: '13px', padding: '8px 0' }}>Senaryo bulunamadı.</p>
        )}
        {defs.length > 0 && (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Senaryo</th>
                <th style={styles.th}>Çeyrek</th>
                <th style={styles.th}>Ekip</th>
                <th style={styles.th}>Pazar / Ürün</th>
                <th style={styles.th}>National Target</th>
                <th style={styles.th}>Ağırlık (Ü/P)</th>
                <th style={styles.th}>Durum</th>
                <th style={styles.th}>İşlem</th>
              </tr>
            </thead>
            <tbody>
              {defs.map(d => {
                const st = STATUS_STYLE[d.distributionStatus] || { color: '#475569', bg: '#f1f5f9', label: d.distributionStatus || '—' };
                const busy = busyId === d.id;
                const locked = d.distributionStatus === 'Published';
                const isOpen = editId === d.id;
                return (
                  <React.Fragment key={d.id}>
                    <tr>
                      <td style={styles.td}>{d.versionName || <span style={{ color: '#94a3b8' }}>(adsız)</span>}</td>
                      <td style={styles.td}>{d.yearQuarter}</td>
                      <td style={styles.td}><span style={styles.tenantBadge}>{d.teamName}</span></td>
                      <td style={styles.td}>
                        <div style={{ fontSize: '13px' }}>{d.productName}</div>
                        <div style={{ fontSize: '12px', color: '#94a3b8' }}>{d.marketName}</div>
                      </td>
                      <td style={styles.td}>{fmtNum(d.nationalTarget)}</td>
                      <td style={styles.td}>{fmtNum(d.productWeight, 0)} / {fmtNum(d.marketWeight, 0)}</td>
                      <td style={styles.td}>
                        <span style={{ color: st.color, backgroundColor: st.bg, padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600 }}>
                          {st.label}
                        </span>
                        {d.resultRowCount > 0 && (
                          <span style={{ fontSize: '11px', color: '#94a3b8', marginLeft: '6px' }}>{d.resultRowCount} satır</span>
                        )}
                      </td>
                      <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                        {d.distributionStatus === 'Distributing' ? (
                          <span style={{ color: '#7c3aed', fontSize: '12px' }}>Arka planda çalışıyor…</span>
                        ) : (
                          <>
                            <button style={{ ...(isOpen ? styles.primaryButton : styles.pageButton), padding: '6px 10px', marginRight: '4px' }}
                              onClick={() => openEdit(d)}>
                              {locked ? 'Görüntüle' : (isOpen ? 'Kapat' : 'Düzenle')}
                            </button>
                            {d.distributionStatus === 'Distributed' && (
                              <button style={{ ...styles.primaryButton, padding: '6px 10px', marginRight: '4px' }} disabled={busy}
                                onClick={() => runAction(d.id, () => publishTarget(d.id, tid), 'Senaryo yayınlandı ve kilitlendi.')}>
                                Yayınla
                              </button>
                            )}
                            {locked && (
                              <button style={{ ...styles.pageButton, marginRight: '4px' }} disabled={busy}
                                onClick={() => runAction(d.id, () => unpublishTarget(d.id, tid), 'Kilit açıldı.')}>
                                Kilidi Aç
                              </button>
                            )}
                            {!locked && (
                              <button style={styles.dangerButton} disabled={busy}
                                onClick={() => runAction(d.id, () => deleteTargetScenario(d.id, tid), 'Senaryo silindi.')}>
                                Sil
                              </button>
                            )}
                          </>
                        )}
                      </td>
                    </tr>

                    {isOpen && (
                      <tr>
                        <td colSpan={8} style={{ ...styles.td, backgroundColor: '#f8fafc', padding: '16px' }}>
                          {editError && <p style={{ color: '#dc2626', fontSize: '13px', margin: '0 0 10px 0' }}>{editError}</p>}

                          {/* Düzenlenebilir parametreler */}
                          {editForm && (
                            <div style={{ marginBottom: '16px' }}>
                              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                <div>
                                  <label style={styles.label}>Senaryo Adı</label>
                                  <input style={styles.input} value={editForm.versionName} disabled={locked}
                                    onChange={e => setEF('versionName', e.target.value)} />
                                </div>
                                <div>
                                  <label style={styles.label}>National Target *</label>
                                  <input type="number" min="1" style={{ ...styles.input, minWidth: '120px' }} value={editForm.nationalTarget}
                                    disabled={locked} onChange={e => setEF('nationalTarget', e.target.value)} />
                                </div>
                                <div>
                                  <label style={styles.label}>Pazar Ağırlığı (%)</label>
                                  <input type="number" step="0.01" min="0" style={{ ...styles.input, width: '110px', minWidth: '110px' }}
                                    value={editForm.marketWeight} disabled={locked} onChange={e => setEF('marketWeight', e.target.value)} />
                                </div>
                                <div>
                                  <label style={styles.label}>Ürün Ağırlığı (%)</label>
                                  <input type="number" step="0.01" min="0" style={{ ...styles.input, width: '110px', minWidth: '110px' }}
                                    value={editForm.productWeight} disabled={locked} onChange={e => setEF('productWeight', e.target.value)} />
                                </div>
                              </div>
                              <div style={{ marginTop: '12px' }}>
                                <label style={styles.label}>Referans Aylar *</label>
                                {renderMonthPicker(editForm.calcMonths, toggleEditMonth, locked)}
                              </div>

                              <div style={{ marginTop: '14px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                                {!locked && (
                                  <>
                                    <button style={styles.pageButton} disabled={editSaving}
                                      onClick={() => handleSaveEdit(d.id, false)}>
                                      {editSaving ? '...' : 'Kaydet'}
                                    </button>
                                    <button style={styles.primaryButton} disabled={editSaving}
                                      onClick={() => handleSaveEdit(d.id, true)}>
                                      {editSaving ? 'İşleniyor...' : 'Kaydet ve Dağıt'}
                                    </button>
                                  </>
                                )}
                                <button style={styles.pageButton} disabled={exporting || resultRows.length === 0}
                                  onClick={() => handleExport(d.id)}>
                                  {exporting ? 'Aktarılıyor...' : '📥 Excel’e Aktar'}
                                </button>
                                {locked && (
                                  <span style={{ fontSize: '12px', color: '#15803d' }}>🔒 Yayınlı — salt-okunur. Düzenlemek için kilidi açın.</span>
                                )}
                              </div>
                              {!locked && d.distributionStatus === 'Distributed' && (
                                <p style={{ fontSize: '12px', color: '#b45309', margin: '8px 0 0 0' }}>
                                  Parametreleri değiştirdiyseniz sonucu güncellemek için “Kaydet ve Dağıt” kullanın.
                                </p>
                              )}
                            </div>
                          )}

                          {/* Sonuç tablosu */}
                          {resultLoading
                            ? <p style={{ color: '#64748b', margin: 0 }}>Sonuç yükleniyor...</p>
                            : resultRows.length === 0
                              ? <p style={{ color: '#94a3b8', margin: 0 }}>Henüz dağılım sonucu yok. “Kaydet ve Dağıt” ile oluşturun.</p>
                              : (
                                <div style={{ overflowX: 'auto' }}>
                                  <table style={styles.table}>
                                    <thead>
                                      <tr>
                                        {['Bölge', 'Temsilci', 'Brick', 'Brick Paylaşım %',
                                          'Ürün Ref. Dönem Satış', 'Ürün Coğrafi Pay',
                                          'Pazar Ref. Dönem Satış', 'Pazar Coğrafi Pay',
                                          'Ağırlıklı Pay', 'Brick Hedef', 'Nihai Hedef'].map(h => (
                                          <th key={h} style={{ ...styles.th, whiteSpace: 'nowrap' }}>{h}</th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {resultRows.map(r => (
                                        <tr key={r.id}>
                                          <td style={styles.td}>{r.regionName}</td>
                                          <td style={styles.td}>{r.repName}</td>
                                          <td style={styles.td}>{r.brickName}</td>
                                          <td style={styles.td}>{fmtNum(r.salesPercent)}</td>
                                          <td style={styles.td}>{fmtNum(r.productSalesRefMonths, 2)}</td>
                                          <td style={styles.td}>{fmtNum(r.productGeoShare, 4)}</td>
                                          <td style={styles.td}>{fmtNum(r.marketSalesRefMonths, 2)}</td>
                                          <td style={styles.td}>{fmtNum(r.marketGeoShare, 4)}</td>
                                          <td style={styles.td}>{fmtNum(r.weightedGeoShare, 4)}</td>
                                          <td style={styles.td}>{fmtNum(r.brickTarget, 2)}</td>
                                          <td style={{ ...styles.td, fontWeight: 600 }}>{fmtNum(r.brickTargetShared, 2)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default TargetDistributionPanel;
